import { create } from "zustand"

import type { ChatSlice } from "./slices/chat-slice"
import type { ExplorerFeaturesSlice } from "./slices/explorer-features-slice"
import type { DashboardSpec } from "@/features/dash/dashboard/catalog"

import { createChatSlice } from "./slices/chat-slice"
import { createExplorerFeaturesSlice } from "./slices/explorer-features-slice"
import { createNamespacedStore } from "./store-factory"

export type DashboardMode = "chat" | "landing" | "view"

type DashStore = ChatSlice &
  ExplorerFeaturesSlice & {
    uncancellableFlowRunSessionIds: ReadonlyMap<string, number>
    markUncancellableFlowRun: (sessionId: string) => void
    clearUncancellableFlowRun: (sessionId: string) => void
    // Dashboard-specific state
    currentDashboardSpec: DashboardSpec | null
    setDashboardSpec: (spec: DashboardSpec | null) => void
    currentDashboardId: string | null
    setCurrentDashboardId: (id: string | null) => void
    currentDashboardTitle: string
    setCurrentDashboardTitle: (title: string) => void
    currentDashboardType: "personal" | "company"
    setCurrentDashboardType: (type: "personal" | "company") => void
    dashboardMode: DashboardMode
    setDashboardMode: (mode: DashboardMode) => void
    dashboardPanelVisible: boolean
    setDashboardPanelVisible: (visible: boolean) => void
    updateOnLoad: boolean
    setUpdateOnLoad: (enabled: boolean) => void
    lastUpdated: Date | null
    setLastUpdated: (date: Date | null) => void
    loadDashboard: (
      id: string,
      spec: DashboardSpec,
      options?: {
        updateOnLoad?: boolean
        dashboardType?: "personal" | "company"
        title?: string
        qualityReview?: Record<string, unknown> | null
        qualityHistory?: Array<Record<string, unknown>>
      }
    ) => void
    currentDashboardQualityReview: Record<string, unknown> | null
    currentDashboardQualityHistory: Array<Record<string, unknown>>
    setCurrentDashboardQualityReview: (review: Record<string, unknown> | null) => void
    setCurrentDashboardQualityHistory: (history: Array<Record<string, unknown>>) => void
    pendingAutoRefresh: boolean
    clearPendingAutoRefresh: () => void
    isDashboardRefreshing: boolean
    setDashboardRefreshing: (refreshing: boolean) => void
    pendingSaveAfterRefresh: boolean
    setPendingSaveAfterRefresh: (pending: boolean) => void
    selectedTileIndex: number | null
    setSelectedTileIndex: (index: number | null) => void
    lastError: string | null
    setLastError: (error: string | null) => void
    clearError: () => void
    pendingNewDashboard: boolean
    dashboardDocumentGeneration: number
    beginNewDashboardDocument: () => void
    requestNewDashboard: () => void
  }

/**
 * Factory function to create a new Dash store instance.
 * Used internally by createNamespacedStore to create per-sessionType instances.
 */
function createDashStoreInstance() {
  return create<DashStore>()((set, get, api) => ({
    ...createChatSlice(set, get, api),
    ...createExplorerFeaturesSlice(set, get, api),
    uncancellableFlowRunSessionIds: new Map(),
    markUncancellableFlowRun(sessionId) {
      set((state) => {
        const sessionIds = new Map(state.uncancellableFlowRunSessionIds)
        sessionIds.set(sessionId, (sessionIds.get(sessionId) ?? 0) + 1)
        return { uncancellableFlowRunSessionIds: sessionIds }
      })
    },
    clearUncancellableFlowRun(sessionId) {
      set((state) => {
        const count = state.uncancellableFlowRunSessionIds.get(sessionId)
        if (count === undefined) return state
        const sessionIds = new Map(state.uncancellableFlowRunSessionIds)
        if (count === 1) sessionIds.delete(sessionId)
        else sessionIds.set(sessionId, count - 1)
        return { uncancellableFlowRunSessionIds: sessionIds }
      })
    },
    // Dashboard-specific state
    currentDashboardSpec: null,
    setDashboardSpec(spec) {
      set({ currentDashboardSpec: spec, lastUpdated: spec ? new Date() : null })
    },
    currentDashboardId: null,
    setCurrentDashboardId(id) {
      set({ currentDashboardId: id })
    },
    currentDashboardTitle: "",
    setCurrentDashboardTitle(title) {
      set({ currentDashboardTitle: title })
    },
    currentDashboardType: "personal",
    setCurrentDashboardType(type) {
      set({ currentDashboardType: type })
    },
    dashboardMode: "landing",
    setDashboardMode(mode) {
      set({ dashboardMode: mode })
    },
    dashboardPanelVisible: true,
    setDashboardPanelVisible(visible) {
      set({ dashboardPanelVisible: visible })
    },
    updateOnLoad: false,
    setUpdateOnLoad(enabled) {
      set({ updateOnLoad: enabled })
    },
    lastUpdated: null,
    setLastUpdated(date) {
      set({ lastUpdated: date })
    },
    loadDashboard(id, spec, options) {
      const resolvedUpdateOnLoad = options?.updateOnLoad ?? false
      set((state) => ({
        currentDashboardId: id,
        currentDashboardSpec: spec,
        currentDashboardTitle: options?.title ?? "",
        currentDashboardType: options?.dashboardType ?? "personal",
        dashboardMode: "view",
        dashboardPanelVisible: true,
        updateOnLoad: resolvedUpdateOnLoad,
        pendingAutoRefresh: resolvedUpdateOnLoad,
        pendingSaveAfterRefresh: false,
        isDashboardRefreshing: false,
        selectedTileIndex: null,
        lastError: null,
        currentDashboardQualityReview: options?.qualityReview ?? null,
        currentDashboardQualityHistory: options?.qualityHistory ?? [],
        // Opening an existing dashboard is a document boundary just like
        // starting a new one. Any create/autosave that began for the
        // previous document must not be allowed to restore its id later.
        dashboardDocumentGeneration: state.dashboardDocumentGeneration + 1,
      }))
    },
    currentDashboardQualityReview: null,
    currentDashboardQualityHistory: [],
    setCurrentDashboardQualityReview(review) {
      set({ currentDashboardQualityReview: review })
    },
    setCurrentDashboardQualityHistory(history) {
      set({ currentDashboardQualityHistory: history })
    },
    pendingAutoRefresh: false,
    clearPendingAutoRefresh() {
      set({ pendingAutoRefresh: false })
    },
    isDashboardRefreshing: false,
    setDashboardRefreshing(refreshing) {
      set({ isDashboardRefreshing: refreshing })
    },
    pendingSaveAfterRefresh: false,
    setPendingSaveAfterRefresh(pending) {
      set({ pendingSaveAfterRefresh: pending })
    },
    selectedTileIndex: null,
    setSelectedTileIndex(index) {
      set({ selectedTileIndex: index })
    },
    lastError: null,
    setLastError(error) {
      set({ lastError: error })
    },
    clearError() {
      set({ lastError: null })
    },
    pendingNewDashboard: false,
    dashboardDocumentGeneration: 0,
    beginNewDashboardDocument() {
      set((state) => ({
        dashboardDocumentGeneration: state.dashboardDocumentGeneration + 1,
      }))
    },
    requestNewDashboard() {
      set((state) => ({
        dashboardMode: "landing",
        dashboardPanelVisible: false,
        currentDashboardSpec: null,
        currentDashboardId: null,
        currentDashboardTitle: "",
        currentDashboardType: "personal",
        currentDashboardQualityReview: null,
        currentDashboardQualityHistory: [],
        lastUpdated: null,
        pendingNewDashboard: true,
        pendingSaveAfterRefresh: false,
        pendingAutoRefresh: false,
        updateOnLoad: false,
        isDashboardRefreshing: false,
        selectedTileIndex: null,
        lastError: null,
        dashboardDocumentGeneration: state.dashboardDocumentGeneration + 1,
      }))
    },
  }))
}

/**
 * Hook to get Dash store isolated by sessionType.
 * Each sessionType gets its own store instance.
 *
 * @param sessionType - Session type identifier
 * @returns Zustand store hook for the specific sessionType
 */
export function useDashStore(sessionType: string) {
  return createNamespacedStore("dash", sessionType, createDashStoreInstance)
}
